import {
  DTypeDTO,
  DTypeDTOFireProtectionTypeEnum,
  MTypeDTO,
  MTypeDTOFireProtectionTypeEnum,
} from '~/api/webservice';
import {
  BendingMomentTypeProductDto,
  HeightOffsetTypeProductDto,
  IBendingMomentProperties,
  IProductProperties,
  ISOPROProduct,
} from '~/models';
import { useProductsStore } from '~/store';

/** */
export class ISOPROProductBendingMomentType extends ISOPROProduct {
  protected readonly _shearForceLoadBearingCapacity:
    | [number]
    | [number, number];
  readonly shearForceLoadBearingCapacityCode: BendingMomentTypeProductDto["shearForceLoadBearingCapacityCode"];
  declare readonly fireProtectionType: BendingMomentTypeProductDto["fireProtectionType"];

  declare readonly properties: IProductProperties & {
    dimensions: IProductProperties["dimensions"];
    construction: {
      thrustBearings: {
        numberOfThrustShearPosBearing: BendingMomentTypeProductDto["numberOfThrustShearPosBearing"];
        numberOfThrustBearings: BendingMomentTypeProductDto["numberOfThrustBearing"];
        kindOfThrustBearing: BendingMomentTypeProductDto["kindOfThrustBearing"];
      };
      tensionRods: {
        diameter: BendingMomentTypeProductDto["diameterOfTensionRod"];
        number: BendingMomentTypeProductDto["numberOfTensionRods"];
      };
    };
  };

  constructor (data: MTypeDTO | DTypeDTO | HeightOffsetTypeProductDto) {
    super(data);

    this.shearForceLoadBearingCapacityCode = data.shearForceLoadBearingCapacityCode;
    const [_, ...values] = data.shearForceLoadBearingCapacityCode.split("Q");
    this._shearForceLoadBearingCapacity = [+values[0]];
    if (values[1]) this._shearForceLoadBearingCapacity.push(+values[1]);

    this.properties.construction = {
      thrustBearings: {
        numberOfThrustBearings: data.numberOfThrustBearing,
        numberOfThrustShearPosBearing: data.numberOfThrustShearPosBearing,
        kindOfThrustBearing: data.kindOfThrustBearing,
      },
      tensionRods: {
        diameter: data.diameterOfTensionRod,
        number: data.numberOfTensionRods,
      },
    };
  }

  getBiggerShearForceCapacityProduct (): ISOPROProductBendingMomentType | null {
    try {
      const second = this._shearForceLoadBearingCapacity[1];
      const newRowKey = `Q${this._shearForceLoadBearingCapacity[0] + 2}${second ? `Q${second}` : ""
        }`;
      const rowKey = this.rowKey.replace(
        this.shearForceLoadBearingCapacityCode,
        newRowKey
      );
      const product = useProductsStore().productsMap.get(rowKey);
      if (product && !(product instanceof ISOPROProductBendingMomentType))
        throw new Error("");
      return (product as ISOPROProductBendingMomentType) || null;
    } catch (e) {
      console.error(e);
      return null;
    }
  }
}

/** */
export class ISOPROProductMType extends ISOPROProductBendingMomentType {
  declare readonly type: MTypeDTO["type"];
  declare readonly height: MTypeDTO["height"];
  readonly fireProtectionType: MTypeDTO["fireProtectionType"];
  declare readonly capacity: MTypeDTO["capacity"];

  declare readonly properties: {
    dimensions: IBendingMomentProperties["dimensions"];
    construction: {
      thrustBearings: ISOPROProductBendingMomentType['properties']["construction"]["thrustBearings"] & {
        numberOfThrustShearNegBearing: MTypeDTO["numberOfThrustShearNegBearing"];
        numberOfThrustBearing: MTypeDTO['numberOfThrustBearing']
      };
      tensionRods: IBendingMomentProperties["construction"]["tensionRods"];
    };
  }

  constructor (data: MTypeDTO) {
    super(data);
    this.fireProtectionType = data.fireProtectionType;
    this.properties.construction.thrustBearings.numberOfThrustShearNegBearing = data.numberOfThrustShearNegBearing;
  }

  getOneWithoutFireProtection (): ISOPROProductMType | null {
    if (this.hasFireProtection) return null;
    const rowKey = `${this.rowKey} ${MTypeDTOFireProtectionTypeEnum.Rei120}`;
    return (useProductsStore().productsMap.get(rowKey) as ISOPROProductMType) || null;
  }
}

/** */
export class ISOPROProductDType extends ISOPROProductBendingMomentType {
  declare readonly type: DTypeDTO["type"];
  declare readonly height: DTypeDTO["height"];
  readonly fireProtectionType: DTypeDTO["fireProtectionType"];
  declare readonly capacity: DTypeDTO["capacity"];
  declare readonly properties: {
    dimensions: IBendingMomentProperties["dimensions"];
    construction: {
      thrustBearings: ISOPROProductBendingMomentType['properties']["construction"]["thrustBearings"] & {
        diameterOfThrustBearing: DTypeDTO["diameterOfThrustBearing"];
        diameterOfThrustShearNegBearing: DTypeDTO["diameterOfThrustShearNegBearing"];
        diameterOfThrustShearPosBearing: DTypeDTO["diameterOfThrustShearPosBearing"];
        numberOfThrustShearNegBearing: DTypeDTO["numberOfThrustShearNegBearing"];
      };
      tensionRods: IBendingMomentProperties["construction"]["tensionRods"];
    };
  }

  constructor (data: DTypeDTO) {
    super(data);
    this.fireProtectionType = data.fireProtectionType;

    this.properties.construction.thrustBearings = {
      ...this.properties.construction.thrustBearings,
      diameterOfThrustBearing: data.diameterOfThrustBearing,
      diameterOfThrustShearNegBearing: data.diameterOfThrustShearNegBearing,
      diameterOfThrustShearPosBearing: data.diameterOfThrustShearPosBearing,
      numberOfThrustShearNegBearing: data.numberOfThrustShearNegBearing,
    }
  }

  getOneWithoutFireProtection (): ISOPROProductDType | null {
    if (this.hasFireProtection) return null;
    const rowKey = `${this.rowKey} ${DTypeDTOFireProtectionTypeEnum.R90}`;
    return (useProductsStore().productsMap.get(rowKey) as ISOPROProductDType) || null;
  }
}
